import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { controlHeight, fonts, radius } from '@/shared/theme';
import { Icon } from '@/shared/ui';
import { usePaletteStore } from '../model/palette-store';
import { type Command, filterCommands, useCommands } from '../model/use-commands';

/** Keeps the list short enough to scan without scrolling, as the prototype did. */
const MAX_ITEMS = 14;

/**
 * Global command palette (Ctrl/Cmd+K): jump to any screen the user may open, or run a global
 * action. Arrow keys move, Enter runs, Escape closes.
 */
export function CommandPalette() {
  const { t } = useTranslation();
  const open = usePaletteStore((state) => state.open);
  const setOpen = usePaletteStore((state) => state.setOpen);
  const toggle = usePaletteStore((state) => state.toggle);

  const [search, setSearch] = useState('');
  const [index, setIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const commands = useCommands();
  const items = useMemo(
    () => filterCommands(commands, search).slice(0, MAX_ITEMS),
    [commands, search],
  );

  // Ctrl/Cmd+K from anywhere. Capture phase so it still works from inside an input.
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        toggle();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [toggle]);

  // Every opening starts from a clean search, with the caret already in the input. Focusing
  // imperatively rather than with `autoFocus` keeps the behaviour tied to the dialog opening.
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setIndex(0);
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const safeIndex = Math.min(index, Math.max(0, items.length - 1));

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${safeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [safeIndex]);

  const run = (command: Command | undefined) => {
    if (!command) return;
    setOpen(false);
    command.run();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIndex((value) => Math.min(items.length - 1, value + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIndex((value) => Math.max(0, value - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      run(items[safeIndex]);
    }
  };

  let lastGroup: string | null = null;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: 620,
            maxWidth: '94vw',
            m: 2,
            borderRadius: radius.card,
            alignSelf: 'flex-start',
            mt: '11vh',
          },
        },
      }}
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: '15px 18px',
          borderBottom: `1px solid ${theme.vars.palette.md.border}`,
        })}
      >
        <Box
          sx={(theme) => ({ display: 'flex', flex: 'none', color: theme.vars.palette.md.primary })}
        >
          <Icon name="search" size={18} />
        </Box>
        <Box
          component="input"
          ref={inputRef}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setIndex(0);
          }}
          onKeyDown={onKeyDown}
          placeholder={t('palette.placeholder')}
          aria-label={t('palette.placeholder')}
          sx={(theme) => ({
            flex: 1,
            minWidth: 0,
            border: 0,
            outline: 0,
            background: 'transparent',
            color: theme.vars.palette.md.text,
            font: 'inherit',
            fontSize: 15,
          })}
        />
        <Box
          sx={(theme) => ({
            flex: 'none',
            fontSize: 10.5,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color: theme.vars.palette.md.faint,
            display: { xs: 'none', sm: 'block' },
          })}
        >
          {t('palette.hint')}
        </Box>
      </Box>

      <Box ref={listRef} sx={{ maxHeight: '56vh', overflowY: 'auto', p: 1 }}>
        {items.length === 0 ? (
          <Box
            sx={{ p: '26px 12px', textAlign: 'center', fontSize: 13.5, color: 'text.secondary' }}
          >
            {t('palette.empty')}
          </Box>
        ) : (
          items.map((command, position) => {
            const showGroup = command.group !== lastGroup;
            lastGroup = command.group;
            const active = position === safeIndex;
            return (
              <Box key={command.id}>
                {showGroup && (
                  <Box
                    sx={(theme) => ({
                      p: '10px 12px 5px',
                      fontSize: 10,
                      letterSpacing: '.14em',
                      textTransform: 'uppercase',
                      fontWeight: 800,
                      color: theme.vars.palette.md.faint,
                    })}
                  >
                    {t(`palette.groups.${command.group}`)}
                  </Box>
                )}
                <Box
                  component="button"
                  type="button"
                  data-index={position}
                  onClick={() => run(command)}
                  onMouseEnter={() => setIndex(position)}
                  sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                    p: '10px 12px',
                    border: 0,
                    borderRadius: radius.control,
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit',
                    color: theme.vars.palette.md.text,
                    background: active ? theme.vars.palette.md.primary050 : 'transparent',
                  })}
                >
                  <Box
                    sx={(theme) => ({
                      display: 'flex',
                      flex: 'none',
                      color: active ? theme.vars.palette.md.primary : theme.vars.palette.md.faint,
                    })}
                  >
                    <Icon name={command.icon} size={16} />
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 13.5,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {command.label}
                  </Box>
                  {command.sub && (
                    <Box
                      component="span"
                      sx={(theme) => ({
                        flex: 'none',
                        fontSize: 11.5,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        color: theme.vars.palette.md.muted,
                      })}
                    >
                      {command.sub}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Dialog>
  );
}

/** Header affordance that opens the palette, showing the shortcut on wide screens. */
export function PaletteButton() {
  const { t } = useTranslation();
  const toggle = usePaletteStore((state) => state.toggle);
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <Box
      component="button"
      type="button"
      onClick={toggle}
      aria-label={t('palette.open')}
      sx={(theme) => ({
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        gap: 1.25,
        height: controlHeight.md,
        px: '13px',
        flex: 'none',
        borderRadius: radius.control,
        border: `1px solid ${theme.vars.palette.md.border}`,
        background: theme.vars.palette.md.panel,
        color: theme.vars.palette.md.muted,
        font: 'inherit',
        cursor: 'pointer',
        '&:hover': { borderColor: theme.vars.palette.md.borderStrong },
      })}
    >
      <Icon name="search" size={16} />
      <Box component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
        {t('palette.open')}
      </Box>
      <Box
        component="span"
        sx={(theme) => ({
          fontFamily: fonts.head,
          fontSize: 10.5,
          fontWeight: 800,
          p: '2px 6px',
          borderRadius: radius.xs,
          background: theme.vars.palette.md.panel3,
          color: theme.vars.palette.md.faint,
        })}
      >
        {isMac ? '⌘K' : 'Strg K'}
      </Box>
    </Box>
  );
}
